import { AuraKpi } from '@/types/models/AuraKpiModel';
import { AuraInsight } from '@/types/models/AuraInsightModel';
import { AuraAction } from '@/types/models/AuraActionModel';
import { AuraChart } from '@/types/models/AuraChartModel';
import { AuraVisualizationPlan } from '@/types/models/AuraVisualizationModel';

/**
 * Block-level status produced by the backend ResponseBuilder.
 * Matches `services/responseBuilder.js::BlockStatus` on plataforma-api.
 */
export type AuraBlockStatus = 'complete' | 'partial' | 'unavailable' | 'error';

/**
 * Top-level AURA response status.
 * Matches `services/responseBuilder.js::ResponseStatus` on plataforma-api.
 */
export type AuraResponseStatus = 'success' | 'partial' | 'blocked' | 'no_data' | 'error';

export interface AuraSummaryBlock {
    status: AuraBlockStatus;
    content?: string;
    highlights?: string[];
}

export interface AuraKpiBlock {
    status: AuraBlockStatus;
    items?: AuraKpi[];
}

export interface AuraInsightBlock {
    status: AuraBlockStatus;
    items?: AuraInsight[];
}

export interface AuraActionBlock {
    status: AuraBlockStatus;
    items?: AuraAction[];
}

export interface AuraChartBlock extends Partial<AuraChart> {
    status: AuraBlockStatus;
}

export interface AuraCitationBlock {
    status: AuraBlockStatus;
    sources?: Array<{
        callId?: string | number;
        timestamp?: string;
        snippet?: string;
        [key: string]: unknown;
    }>;
}

export interface AuraFilterConflict {
    type: 'agent' | 'queue';
    filterKey: 'userId' | 'queueId';
    extractedValue: string;
    status: 'resolved' | 'disambiguation' | 'not_found';
    resolvedId: number | null;
    resolvedName: string | null;
    matches: Array<{ id: number; name: string }>;
}

export interface AuraStatusBlock {
    status: AuraResponseStatus;
    code: string;
    message?: string;
    details?: {
        options?: Array<{ id: string | number; label: string; description?: string }>;
        matches?: Array<{ id: number; name: string }>;
        [key: string]: unknown;
    };
    filter_conflict?: AuraFilterConflict;
}

export interface AuraWarning {
    code: string;
    details?: Record<string, unknown>;
}

/**
 * Stable AURA response contract.
 *
 * Every block is ALWAYS present with a `status` field. Frontend code should
 * check `status` before reading block-specific fields (items/content/labels).
 */
export interface AuraResponseBlocks {
    summary: AuraSummaryBlock;
    kpis: AuraKpiBlock;
    insights: AuraInsightBlock;
    actions: AuraActionBlock;
    chart: AuraChartBlock;
    visualization?: AuraVisualizationPlan;
    citations: AuraCitationBlock;
    status: AuraStatusBlock;
    warnings?: AuraWarning[];
    classification?: {
        in_scope?: boolean;
        intent?: string;
        mode?: string;
        filter_conflict?: AuraFilterConflict | null;
        entities?: {
            metrics?: string[];
            dimensions?: string[];
            period?: string | null;
            top?: unknown;
            filters?: Record<string, unknown>;
            callIds?: Array<string | number>;
        };
        time_coherent?: boolean;
        time_conflict?: {
            questionPeriod?: string | null;
            filterPeriod?: string | null;
            details?: string | null;
        } | null;
        reason?: string | null;
        confidence?: number;
        costUsd?: number;
        tokens_used?: number;
    };
}
